import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslate } from '../../translate/I18n';
import { useNumber } from '../../hooks/useNumber';
import { useTerritory } from '../../hooks/useTerritory';

import Box from '../atoms/Box';
import Text from '../atoms/Text';
import LoadingBox from '../atoms/LoadingBox';

import CheckBadge from '../atoms/icons/CheckBadge';

const StyledGrid = styled('div')(({ theme }) => ({
  flex: 1,
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  paddingLeft: theme.space[3],
  paddingRight: theme.space[3],
  textAlign: 'right',
  background: theme.colors.white,
  boxShadow: `0 0 10px 5px ${theme.colors.white}`,
  alignContent: 'center',
  marginLeft: -theme.space[3],
  marginBottom: theme.space[3],
  div: {
    paddingTop: theme.space[2],
    paddingBottom: theme.space[2],
    '&:nth-child(n+4):not(:nth-last-child(2))': {
      borderRight: '1px solid #F1F1F1',
      paddingRight: theme.space[2]
    }
  },
  [`@media (max-width: ${theme.breakpoints.lg})`]: {
    padding: theme.space[1],
    marginLeft: 0,
    div: {
      padding: '16px 12px'
    }
  }
}));

const StyledGridLeftHeader = styled(Box)(({ theme }) => ({
  fontWeight: 700,
  textAlign: 'left',
  fontSize: '16px',
  [`@media (max-width: ${theme.breakpoints.lg})`]: {
    paddingLeft: theme.space[1],
    borderLeft: `1px solid ${theme.colors.greyVerylight}`
  }
}));

const loadingBoxProps = {
  height: '10px',
  width: '70px',
  ml: 'auto',
  borderRadius: '10px',
  padding: '0 !important'
};

const CopyrightCalculatorResults = ({ resultsCalculation }) => {
  const { formatNumber } = useNumber();
  const territory = useTerritory();
  const { t } = useTranslate(['copyrightCalculator']);

  const getFormatedResult = (regime, attribute) => {
    return resultsCalculation
      ? formatNumber(resultsCalculation?.[regime]?.[attribute]) + territory.currencySymbol
      : null;
  };

  return (
    <StyledGrid flex="1" display="grid" grid-template-columns="repeat(3, 1fr)">
      <Text />
      <Box>
        <Text color="greyDark" fontSize="16px" fontWeight="700" mt={{ _: '22px', lg: 0 }}>
          {t('copyrightCalculator:withoutAuthorsRegime')}
        </Text>
      </Box>
      <Box
        display="flex"
        alignItems={{ _: 'flex-end', lg: 'center' }}
        flexDirection={{ _: 'column', lg: 'row' }}
        justifyContent="flex-end">
        <CheckBadge color="main" />
        <Text ml={1} fontSize="16px" fontWeight="700" mt={{ _: 1, lg: 0 }}>
          {t('copyrightCalculator:withAuthorsRegime')}
        </Text>
      </Box>

      <StyledGridLeftHeader borderTop="1px solid #F1F1F1" paddingLeft={3} borderTopLeftRadius={{ _: 2, lg: '0px' }}>
        <Text>{t('copyrightCalculator:authorRightIncome')}</Text>
      </StyledGridLeftHeader>
      <Box borderTop="1px solid #F1F1F1">
        <Text fontSize="20px" color="greyDark">
          -
        </Text>
      </Box>
      <Box textAlign="right" borderTop="1px solid #F1F1F1" borderTopRightRadius={2}>
        <Text styleName="pH2">
          {getFormatedResult('withRegime', 'authorsightsIncome') || (
            <LoadingBox bg="dark" as="span" display="inline-block" {...loadingBoxProps} />
          )}
        </Text>
      </Box>
      <StyledGridLeftHeader ml={{ _: 0, lg: 3 }}>
        <Text>{t('copyrightCalculator:nsso')}</Text>
      </StyledGridLeftHeader>
      <Box textAlign="right">
        <Text fontSize="20px" color="greyDark">
          {getFormatedResult('withoutRegime', 'NSSO') || (
            <LoadingBox
              bg="greyLight"
              as="span"
              display="inline-block"
              height="10px"
              width="70px"
              ml="auto"
              borderRadius="10px"
              padding="0 !important"
            />
          )}
        </Text>
      </Box>
      <Box textAlign="right">
        <Text fontSize="20px" styleName="pH2">
          {getFormatedResult('withRegime', 'NSSO') || (
            <LoadingBox bg="dark" as="span" display="inline-block" {...loadingBoxProps} />
          )}
        </Text>
      </Box>
      <StyledGridLeftHeader borderBottom="1px solid #F1F1F1" ml={{ _: 0, lg: 3 }}>
        <Text>{t('copyrightCalculator:incomeTaxes')}</Text>
      </StyledGridLeftHeader>
      <Box textAlign="right" borderBottom="1px solid #F1F1F1">
        <Text fontSize="20px" color="greyDark">
          {getFormatedResult('withoutRegime', 'incomeTaxes') || (
            <LoadingBox
              bg="greyLight"
              as="span"
              display="inline-block"
              height="10px"
              width="70px"
              ml="auto"
              borderRadius="10px"
              padding="0 !important"
            />
          )}
        </Text>
      </Box>
      <Box textAlign="right" borderBottom="1px solid #F1F1F1">
        <Text fontSize="20px" styleName="pH2">
          {getFormatedResult('withRegime', 'incomeTaxes') || (
            <LoadingBox bg="dark" as="span" display="inline-block" {...loadingBoxProps} />
          )}
        </Text>
      </Box>
      <StyledGridLeftHeader ml={{ _: 0, lg: 3 }}>
        <Text>{t('copyrightCalculator:netIncome')}</Text>
      </StyledGridLeftHeader>
      <Box textAlign="right">
        <Text fontSize="20px" color="greyDark">
          {getFormatedResult('withoutRegime', 'netIncome') || (
            <LoadingBox
              bg="greyLight"
              as="span"
              display="inline-block"
              height="10px"
              width="70px"
              ml="auto"
              borderRadius="10px"
              padding="0 !important"
            />
          )}
        </Text>
      </Box>
      <Box textAlign="right">
        <Text fontSize="20px" styleName="pH2">
          {getFormatedResult('withRegime', 'netIncome') || (
            <LoadingBox bg="dark" as="span" display="inline-block" {...loadingBoxProps} />
          )}
        </Text>
      </Box>
      <StyledGridLeftHeader
        borderBottom="1px solid #F1F1F1"
        paddingLeft={3}
        gridColumnStart="1"
        gridColumnEnd="3"
        borderBottomLeftRadius={{ _: 2, lg: '0px' }}>
        <Text fontSize="22px">{t('copyrightCalculator:gain')}</Text>
      </StyledGridLeftHeader>
      <Box textAlign="right" borderBottom="1px solid #F1F1F1" borderBottomRightRadius={2}>
        <Text fontWeight="700" color="main" fontSize="24px">
          {getFormatedResult('withRegime', 'gain') || (
            <LoadingBox bg="main" as="span" display="inline-block" {...loadingBoxProps} />
          )}
        </Text>
      </Box>
    </StyledGrid>
  );
};

CopyrightCalculatorResults.propTypes = {
  resultsCalculation: PropTypes.shape({
    withoutRegime: PropTypes.shape({
      NSSO: PropTypes.number,
      incomeTaxes: PropTypes.number,
      netIncome: PropTypes.number
    }),
    withRegime: PropTypes.shape({
      authorsightsIncome: PropTypes.number,
      NSSO: PropTypes.number,
      incomeTaxes: PropTypes.number,
      netIncome: PropTypes.number,
      gain: PropTypes.number
    })
  })
};

export default CopyrightCalculatorResults;
